import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CONTACT_INFO } from "@/data/content";
import { MessageCircle, X, Send } from "lucide-react";

const DISMISS_KEY = "gs-chat-teaser-dismissed";

export const FloatingWhatsApp = () => (
  <a
    href={CONTACT_INFO.whatsapp}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-float-button"
    aria-label="Chat on WhatsApp"
    className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[var(--shadow-elev-2)] hover:scale-105 focus-visible:ring-2 focus-visible:ring-white"
    style={{ transition: "transform 0.2s ease" }}
  >
    <MessageCircle className="h-7 w-7" fill="white" strokeWidth={0} />
  </a>
);

export const LiveChatTeaser = () => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    const timer = setTimeout(() => setVisible(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const sendToWhatsApp = () => {
    const text = message.trim();
    const url = text
      ? `${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(text)}`
      : CONTACT_INFO.whatsapp;
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendToWhatsApp();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.35, ease: [0.21, 0.61, 0.35, 1] }}
          data-testid="live-chat-teaser"
          className="fixed bottom-24 right-6 z-50 w-[320px] rounded-2xl border border-[color:var(--gs-line)] bg-[color:var(--gs-navy-850)] p-4 shadow-[var(--shadow-gold)]"
          role="dialog"
          aria-label="Live chat invitation"
        >
          <button
            onClick={dismiss}
            data-testid="live-chat-teaser-close"
            aria-label="Dismiss chat invitation"
            className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full text-white/40 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              <img
                src="/logo-small.png"
                alt="GrowthSync Solutions"
                className="h-11 w-11 rounded-full border border-[color:var(--gs-line)]"
              />
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[color:var(--gs-navy-850)] bg-[#25D366]" />
            </div>
            <div>
              <p className="font-display text-sm font-semibold text-white">
                Need help growing your business?
              </p>
              <p className="mt-1 text-xs leading-relaxed text-white/60">
                Write us a message — it lands directly in our WhatsApp. We usually
                reply within minutes.
              </p>
            </div>
          </div>

          {/* Message input - sends straight to WhatsApp */}
          <div className="mt-3.5 flex items-end gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Type your message..."
              data-testid="live-chat-teaser-message-input"
              aria-label="Your message"
              className="min-h-[44px] w-full resize-none rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#25D366]/60 focus:outline-none"
            />
            <button
              onClick={sendToWhatsApp}
              data-testid="live-chat-teaser-send"
              aria-label="Send message on WhatsApp"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#25D366] text-white hover:bg-[#1fb857] active:scale-[0.96]"
            >
              <Send className="h-4.5 w-4.5" size={18} />
            </button>
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-[10px] text-white/40">
            <MessageCircle className="h-3 w-3 text-[#25D366]" fill="#25D366" strokeWidth={0} />
            Opens WhatsApp with your message ready to send
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
